import { z } from 'zod';

// API Client Configuration types
export interface APIRequestOptions extends RequestInit {
  cacheEnabled?: boolean;
  bustCache?: boolean;
}

export interface APIClientConfig {
  baseUrl: string;
  defaultHeaders?: Record<string, string>;
  cache?: Map<string, Response>;
  newRelic?: {
    noticeError?: (error: Error, context: Record<string, any>) => void;
  };
  strictValidation?: boolean;
}

// Flow related schemas
// Note: These are simplified schemas. For full Flow type support,
// you may need to expand these based on the complete Flow type definition

const OptionSchema = z.object({
  optionId: z.string(),
  text: z.string(),
  group: z.string().optional(),
  value: z.union([z.string(), z.boolean(), z.null()]).optional(),
  nextQuestionId: z.string().optional(),
  questionId: z.string().optional(),
  leadFieldAssociated: z.string().optional(),
  isPreSelected: z.boolean().optional(),
  isArray: z.boolean().optional(),
  icon: z.string().optional(),
  subtext: z.string().optional(),
});

const PlaceholderSchema = z.object({
  description: z.string(),
  key: z.string(),
  defaultValue: z.string().optional(),
});

const BranchingLogicSchema = z.object({
  type: z.string(),
  nextId: z.string(),
  condition: z.object({
    type: z.string(),
    operator: z.string().optional(),
    optionId: z.string().optional(),
    value: z.string().optional(),
    group: z.string().optional(),
    combination: z.array(z.string()).optional(),
    optionIds: z.array(z.string()).optional(),
  }),
});

const StepSchema = z
  .object({
    customField: z.string().optional(),
    flowId: z.string().optional(),
    isEntry: z.boolean().optional(),
    id: z.string().optional(),
    type: z.string(),
    title: z.string().optional(),
    subtitle: z.string().optional(),
    preSubHeading: z.string().optional(),
    leadFieldAssociated: z.string().optional(),
    branchingLogic: z.array(BranchingLogicSchema).optional(),
    placeholders: z.array(PlaceholderSchema).optional(),
    tooltip: z.string().optional(),
    modal: z
      .object({
        title: z.string().optional(),
        content: z.string().optional(),
        buttonText: z.string().optional(),
        enable: z.boolean().optional(),
      })
      .optional(),
    isTerminal: z.boolean().optional(),
    required: z.boolean().optional(),
    stepName: z.string().optional(),
    nextButtonText: z.string().optional(),
    options: z.array(OptionSchema).optional(),
    questionType: z.string().optional(),
    orientation: z.string().optional(),
    sideRailConfigs: z.array(z.any()).optional(),
    tcpaText: z.string().optional(),
    automaticNext: z.boolean().optional(),
    maxSelection: z.number().optional(),
    // Additional fields can be added as needed
  })
  .passthrough();

export const FlowSchema = z.array(StepSchema);

export const QuestionSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  type: z.string(),
  content: z.string().optional(),
  subtitle: z.string().optional(),
  options: z.array(OptionSchema).optional(),
  placeholders: z.array(PlaceholderSchema).optional(),
  questionType: z.string().optional(),
});

export const DSLConfigStatusSchema = z.enum(['draft', 'published']);
export const DSLConfigTypeSchema = z.enum(['entry', 'result']);
export const DSLConfigOriginSchema = z.enum(['tenant', 'global']);

export const DSLConfigSchema = z
  .object({
    tenantId: z.string().optional(),
    configId: z.string(),
    name: z.string(),
    status: DSLConfigStatusSchema.or(z.string()),
    type: DSLConfigTypeSchema.or(z.string()),
    version: z.number(),
    data: z.record(z.unknown()),
    componentCount: z.number().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    createdBy: z.string().optional(),
    publishedBy: z.string().optional(),
    origin: DSLConfigOriginSchema.or(z.string()).optional(),
  })
  .passthrough();

export const DSLConfigListSchema = z.array(DSLConfigSchema);

// Type exports for use in TypeScript
export type Flow = z.infer<typeof FlowSchema>;
export type Step = z.infer<typeof StepSchema>;
export type Option = z.infer<typeof OptionSchema>;
export type QuestionResponse = z.infer<typeof QuestionSchema>;
export type DSLConfig = z.infer<typeof DSLConfigSchema>;
export type DSLConfigStatus = z.infer<typeof DSLConfigStatusSchema>;
export type DSLConfigType = z.infer<typeof DSLConfigTypeSchema>;
export type DSLConfigOrigin = z.infer<typeof DSLConfigOriginSchema>;
export type ListDSLConfigsOptions = {
  type?: DSLConfigType;
  status?: DSLConfigStatus;
};
export type Question = {
  id: string;
  type: string;
  title: string | undefined;
  subtitle: string | undefined;
  options: string[] | undefined;
  placeholders: z.infer<typeof PlaceholderSchema>[] | undefined;
  questionType: string | undefined;
};
